import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getEffectiveLevel } from '@/lib/defaultRoute';
import { ContextRequired } from '@/features/context';

/**
 * RequireContext — route guard for pages whose data is org/network scoped.
 *
 * An authenticated user with NO context cannot load any of it: every
 * context-scoped endpoint answers 400 "No organization or network context
 * selected". Before this guard the page mounted anyway, fired a burst of 400s
 * (prod condition 28, 255x since 2026-08-29) and rendered nothing — the SSO
 * user in vingreact#64 landed on `/` in exactly that state and made no further
 * request. The guard renders the context chooser instead, so the context-scoped
 * children never mount and the user always has something to act on.
 *
 * Not a gate for:
 *  - unauthenticated renders — `ProtectedRoute` owns that decision and would
 *    otherwise be second-guessed here;
 *  - effective viewers — the learner surfaces (inbox, certificates, library)
 *    are identity-scoped, not context-scoped, and work with no context at all.
 */
export function RequireContext({ children }: { children: React.ReactNode }) {
  const { user, currentContext } = useCurrentUser();

  if (!user || currentContext) return <>{children}</>;
  if (getEffectiveLevel(user) === 'viewer') return <>{children}</>;

  return <ContextRequired />;
}
